import React, { useMemo, useState } from 'react';
import {
  ArrowDown, ArrowUp, MessageSquarePlus, Plus, Search, X,
} from 'lucide-react';

import { instructionFieldName, pickableStages } from '@/lib/processOrder';
import { Colors } from './theme';

/**
 * The processes a job runs, picked with a thumb.
 *
 * The desktop picker is a 45-row grid whose number box is the control: you type
 * a 3 against Lamination and it takes third place. That is the fastest thing
 * there is with a keyboard, and unusable without one — a number field per row on
 * a phone is 45 tiny targets and a numeric keypad opening over the list.
 *
 * So the same decision, split in two. Underneath: every process this product
 * type can run, searchable, each a full-width row — tap it and it joins the
 * pipeline at the end. Above: what has been picked, in running order, each row
 * carrying the two things you actually want once it is on the list — move it,
 * or take it off.
 *
 * The order is the list's own order, so there is no number to type and none to
 * clash. Moving a row renumbers everything from 1, which is what the server
 * reads (`build_pipeline_from_picks`).
 *
 * Packing and Dispatch never appear: every job is packed and every job goes out,
 * so they are appended whatever else was picked.
 *
 * A picked process can carry one instruction — "Matt BOPP 12 mic" — asked for on
 * its own row, exactly as the desktop asks for it. Picking writes a bare "Yes",
 * which is all the stage rule needs, so the box only appears when there is a
 * real instruction to show or somebody asks for one.
 */

const PICKED = 'Yes';

/** The picked keys in running order — the numbering is a list, not a spelling. */
export function pickedInOrder(processOrder) {
  return Object.entries(processOrder || {})
    .sort((a, b) => a[1] - b[1])
    .map(([key]) => key);
}

/** That list back as `{stage_key: n}`, numbered from 1 with no gaps. */
export function renumber(keys) {
  return Object.fromEntries(keys.map((key, idx) => [key, idx + 1]));
}

function PickedRow({
  rule, position, total, instruction, onMove, onRemove, onInstruction, canInstruct,
}) {
  const [noteOpen, setNoteOpen] = useState(false);
  const hasNote = canInstruct && instruction && instruction !== PICKED;
  const showNote = canInstruct && (hasNote || noteOpen);

  return (
    <li
      data-testid={`picked-${rule.stage_key}`}
      className="border-b last:border-b-0"
      style={{ borderColor: Colors.border.subtle }}
    >
      <div className="flex items-center gap-2 py-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold tabular-nums"
          style={{ backgroundColor: Colors.accent.wash, color: Colors.accent.DEFAULT }}
        >
          {position + 1}
        </span>
        <span className="min-w-0 flex-1 truncate text-[14px] font-semibold" style={{ color: Colors.text.primary }}>
          {rule.stage_name}
        </span>

        {canInstruct && !showNote && (
          <button
            type="button"
            data-testid={`note-${rule.stage_key}`}
            onClick={() => setNoteOpen(true)}
            aria-label={`Add an instruction for ${rule.stage_name}`}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
            style={{ color: Colors.text.tertiary }}
          >
            <MessageSquarePlus className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          data-testid={`up-${rule.stage_key}`}
          onClick={() => onMove(-1)}
          disabled={position === 0}
          aria-label={`Move ${rule.stage_name} earlier`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border disabled:opacity-30"
          style={{ borderColor: Colors.border.default, color: Colors.text.secondary }}
        >
          <ArrowUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          data-testid={`down-${rule.stage_key}`}
          onClick={() => onMove(1)}
          disabled={position === total - 1}
          aria-label={`Move ${rule.stage_name} later`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border disabled:opacity-30"
          style={{ borderColor: Colors.border.default, color: Colors.text.secondary }}
        >
          <ArrowDown className="h-4 w-4" />
        </button>
        <button
          type="button"
          data-testid={`remove-${rule.stage_key}`}
          onClick={onRemove}
          aria-label={`Take ${rule.stage_name} off the pipeline`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border"
          style={{ borderColor: Colors.border.default, color: Colors.status.error }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {showNote && (
        <div className="pb-2 pl-9">
          <input
            data-testid={`instruction-${rule.stage_key}`}
            className="h-11 w-full rounded-[10px] border px-3 text-[13px] outline-none"
            style={{
              backgroundColor: Colors.background.secondary,
              borderColor: Colors.border.default,
              color: Colors.text.primary,
            }}
            // "Yes" is the picked marker, not something anybody wrote
            value={instruction === PICKED ? '' : (instruction ?? '')}
            onChange={(e) => onInstruction(e.target.value || PICKED)}
            onBlur={() => setNoteOpen(false)}
            placeholder="How it is to be done — e.g. Matt BOPP 12 mic"
            aria-label={`${rule.stage_name} instruction`}
          />
        </div>
      )}
    </li>
  );
}

export default function ProcessOrderPicker({
  rules, processOrder, onChange, specs = {}, onSpecChange, fieldsByName = {},
}) {
  const [query, setQuery] = useState('');

  const stages = useMemo(() => pickableStages(rules), [rules]);
  const byKey = useMemo(
    () => Object.fromEntries(stages.map((rule) => [rule.stage_key, rule])),
    [stages],
  );

  const picked = useMemo(
    () => pickedInOrder(processOrder).filter((key) => byKey[key]),
    [processOrder, byKey],
  );

  const available = useMemo(() => {
    const term = query.trim().toLowerCase();
    return stages
      .filter((rule) => processOrder[rule.stage_key] == null)
      .filter((rule) => !term || rule.stage_name.toLowerCase().includes(term));
  }, [stages, processOrder, query]);

  /**
   * Picking a process fills its instruction with a bare "Yes" — enough for the
   * presence test, replaced when someone types the real instruction. Dropping it
   * clears the field, so the specs never describe work nobody asked for.
   */
  const markSpec = (rule, isPicked) => {
    const name = instructionFieldName(rule);
    if (!name || !onSpecChange) return;
    if (isPicked) {
      if (!specs[name]) onSpecChange(name, PICKED);
    } else if (specs[name]) {
      onSpecChange(name, '');
    }
  };

  const add = (rule) => {
    onChange(renumber([...picked, rule.stage_key]));
    markSpec(rule, true);
    setQuery('');
  };

  const remove = (key) => {
    onChange(renumber(picked.filter((k) => k !== key)));
    markSpec(byKey[key], false);
  };

  const move = (key, delta) => {
    const from = picked.indexOf(key);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= picked.length) return;
    const next = [...picked];
    [next[from], next[to]] = [next[to], next[from]];
    onChange(renumber(next));
  };

  if (stages.length === 0) {
    return (
      <p className="py-10 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
        This product type has no processes configured.
      </p>
    );
  }

  return (
    <div>
      <section
        className="mt-3 rounded-[14px] border p-4"
        style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
      >
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>
            Runs through
          </h2>
          <span className="text-[11px]" style={{ color: Colors.text.tertiary }}>
            {picked.length} picked
          </span>
        </div>

        {picked.length === 0 ? (
          <p className="py-6 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
            Nothing picked yet. Tap a process below and it joins the pipeline.
            <br />
            <span className="text-[11px]">
              Leave it empty and the job routes itself off the specifications.
            </span>
          </p>
        ) : (
          <>
            <ul data-testid="picked-list" className="mt-1">
              {picked.map((key, idx) => {
                const rule = byKey[key];
                const name = instructionFieldName(rule);
                return (
                  <PickedRow
                    key={key}
                    rule={rule}
                    position={idx}
                    total={picked.length}
                    canInstruct={!!name && (fieldsByName[name]?.type ?? rule.type) !== 'toggle'}
                    instruction={name ? specs[name] : undefined}
                    onInstruction={(v) => onSpecChange?.(name, v)}
                    onMove={(delta) => move(key, delta)}
                    onRemove={() => remove(key)}
                  />
                );
              })}
            </ul>
            <p className="mt-2 text-[10px]" style={{ color: Colors.text.tertiary }}>
              Packing and Dispatch close every pipeline and are added for you.
            </p>
          </>
        )}
      </section>

      <section
        className="mt-3 rounded-[14px] border p-4"
        style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
      >
        <h2 className="mb-2 text-[15px] font-bold" style={{ color: Colors.text.primary }}>
          Add a process
        </h2>

        <div
          className="flex h-11 items-center gap-2 rounded-[10px] border px-3"
          style={{ backgroundColor: Colors.background.secondary, borderColor: Colors.border.default }}
        >
          <Search className="h-4 w-4 shrink-0" style={{ color: Colors.text.tertiary }} />
          <input
            data-testid="process-search"
            className="min-w-0 flex-1 bg-transparent text-[13px] outline-none"
            style={{ color: Colors.text.primary }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Name a process…"
            aria-label="Search processes"
            type="search"
          />
        </div>

        {available.length === 0 ? (
          <p className="py-6 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
            {query.trim()
              ? `No process left is called “${query.trim()}”.`
              : 'Every process this type runs is already on the pipeline.'}
          </p>
        ) : (
          <ul data-testid="process-list" className="mt-1">
            {available.map((rule) => (
              <li key={rule.stage_key}>
                <button
                  type="button"
                  data-testid={`add-${rule.stage_key}`}
                  onClick={() => add(rule)}
                  className="flex w-full items-center gap-2 border-b py-3 text-left last:border-b-0"
                  style={{ borderColor: Colors.border.subtle }}
                >
                  <Plus className="h-4 w-4 shrink-0" style={{ color: Colors.accent.DEFAULT }} />
                  <span className="min-w-0 flex-1 truncate text-[14px]" style={{ color: Colors.text.primary }}>
                    {rule.stage_name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
